/**
 * Gym Tracker Page
 *
 * Active workout interface with:
 * - Workout type selection (Push, Pull, Leg, Cardio, Other)
 * - Large timer display (starts after first set)
 * - Exercise selection
 * - Set logging with weight/reps/RPE
 * - Three-column layout for desktop
 * - Touch-friendly buttons (44px minimum)
 */

import { useState, useEffect } from 'react';
import { useWorkout, WorkoutType } from '@/hooks/useWorkout';
import { WorkoutTimer } from '@/components/Gym/WorkoutTimer';
import { ExerciseCard } from '@/components/Gym/ExerciseCard';
import { AddSetModal } from '@/components/Gym/AddSetModal';
import { VoiceLogButton } from '@/components/Gym/VoiceLogButton';
import { WorkoutViewModal } from '@/components/common/WorkoutViewModal';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import './GymTracker.css';

const WORKOUT_TYPES: WorkoutType[] = ['Push', 'Pull', 'Leg', 'Arms', 'Cardio', 'Other'];

interface RecentWorkout {
  id: string;
  name: string;
  date: string;
  exercises: number;
  duration: string;
}

export function GymTracker(): JSX.Element {
  const { user } = useAuth();
  const {
    activeWorkout,
    workoutType,
    isSelectingType,
    startWorkout,
    selectWorkoutType,
    addSet,
    updateSet,
    deleteSet,
    renameExercise,
    completeWorkout,
    cancelWorkout,
    elapsedTime,
    timerStarted,
    error,
  } = useWorkout();
  const [showAddSetModal, setShowAddSetModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [viewWorkoutId, setViewWorkoutId] = useState<string | null>(null);
  const [recentWorkouts, setRecentWorkouts] = useState<RecentWorkout[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // Fetch recent workouts from Supabase
  useEffect(() => {
    async function fetchRecentWorkouts(): Promise<void> {
      if (!user) {
        setLoadingRecent(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('workout_logs')
        .select(`
          id,
          started_at,
          completed_at,
          status,
          notes,
          workout_log_sets (
            id,
            exercise_id
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('started_at', { ascending: false })
        .limit(3);

      if (fetchError) {
        console.error('Failed to fetch recent workouts:', fetchError);
        setLoadingRecent(false);
        return;
      }

      const formatted: RecentWorkout[] = (data ?? []).map((workout) => {
        const startedAt = new Date(workout.started_at);
        const completedAt = workout.completed_at ? new Date(workout.completed_at) : null;

        // Calculate duration
        let duration = '0m';
        if (completedAt) {
          const durationMs = completedAt.getTime() - startedAt.getTime();
          const minutes = Math.floor(durationMs / 60000);
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          duration = hours > 0 ? `${hours}h ${remainingMinutes}m` : `${minutes}m`;
        }

        // Format relative date (compare calendar days, not raw time)
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const workoutDay = new Date(startedAt.getFullYear(), startedAt.getMonth(), startedAt.getDate());
        const diffDays = Math.floor((todayStart.getTime() - workoutDay.getTime()) / (1000 * 60 * 60 * 24));
        let dateStr = startedAt.toLocaleDateString();
        if (diffDays === 0) dateStr = 'Today';
        else if (diffDays === 1) dateStr = 'Yesterday';
        else if (diffDays < 7) dateStr = `${diffDays} days ago`;

        // Count unique exercises
        const sets = workout.workout_log_sets as { id: string; exercise_id: string }[];
        const uniqueExercises = new Set(sets.map((s) => s.exercise_id)).size;

        return {
          id: workout.id,
          name: `${workout.notes ?? 'Workout'} Day`,
          date: dateStr,
          exercises: uniqueExercises,
          duration,
        };
      });

      setRecentWorkouts(formatted);
      setLoadingRecent(false);
    }

    fetchRecentWorkouts();
  }, [user, activeWorkout]);

  // Group sets by exercise
  const exerciseGroups =
    activeWorkout?.sets.reduce(
      (acc, set) => {
        if (!acc[set.exercise_name]) {
          acc[set.exercise_name] = [];
        }
        acc[set.exercise_name]?.push(set);
        return acc;
      },
      {} as Record<string, typeof activeWorkout.sets>
    ) ?? {};

  // Calculate stats
  const totalSets = activeWorkout?.sets.length ?? 0;
  const totalVolume =
    activeWorkout?.sets.reduce((sum, set) => sum + (set.weight ?? 0) * (set.reps ?? 0), 0) ?? 0;
  const setsWithIntensity = activeWorkout?.sets.filter((s) => s.intensity !== null) ?? [];
  const avgIntensity =
    setsWithIntensity.length > 0
      ? Math.round(setsWithIntensity.reduce((sum, s) => sum + (s.intensity ?? 0), 0) / setsWithIntensity.length)
      : 0;

  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const renderStars = (intensity: number): string => {
    return '⭐'.repeat(intensity);
  };

  // Empty state - no active workout
  if (!activeWorkout && !isSelectingType) {
    return (
      <div className="gym-page">
        <div className="gym-page__main">
          <div className="gym-tracker gym-tracker--empty">
            <div className="gym-tracker__empty-state">
              <div className="gym-tracker__empty-icon">💪</div>
              <h1 className="gym-tracker__empty-title">Ready to train?</h1>
              <p className="gym-tracker__empty-text">Start a new workout to begin logging sets</p>
              <button type="button" className="gym-tracker__start-button" onClick={startWorkout}>
                Start Workout
              </button>
            </div>
          </div>

          {/* Recent Workouts Section */}
          <div className="gym-tracker__recent-section">
            <h2 className="gym-tracker__section-title">Recent Workouts</h2>
            <div className="gym-tracker__recent-list">
              {loadingRecent ? (
                <div className="gym-tracker__recent-loading">Loading...</div>
              ) : recentWorkouts.length === 0 ? (
                <div className="gym-tracker__recent-empty">No completed workouts yet</div>
              ) : (
                recentWorkouts.map((workout) => (
                  <div
                    key={workout.id}
                    className="gym-tracker__recent-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => setViewWorkoutId(workout.id)}
                    onKeyDown={(e) => e.key === 'Enter' && setViewWorkoutId(workout.id)}
                  >
                    <div className="gym-tracker__recent-info">
                      <span className="gym-tracker__recent-name">{workout.name}</span>
                      <span className="gym-tracker__recent-meta">
                        {workout.date} • {workout.exercises} exercises • {workout.duration}
                      </span>
                    </div>
                    <span className="gym-tracker__recent-arrow">→</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Workout View Modal */}
          {viewWorkoutId && (
            <WorkoutViewModal
              workoutId={viewWorkoutId}
              onClose={() => setViewWorkoutId(null)}
            />
          )}
        </div>

        {/* Right Utility Panel */}
        <aside className="gym-page__utility">
          <div className="utility-panel">
            <div className="utility-panel__section">
              <h3 className="utility-panel__title">Voice Logger</h3>
              <p className="utility-panel__description">
                Log sets hands-free using voice commands
              </p>
              <button type="button" className="utility-panel__button">
                Start Recording
              </button>
            </div>

            <div className="utility-panel__section">
              <h3 className="utility-panel__title">Today's Schedule</h3>
              <div className="utility-panel__schedule">
                <div className="utility-panel__schedule-item utility-panel__schedule-item--current">
                  <span className="utility-panel__schedule-time">10:00 AM</span>
                  <span className="utility-panel__schedule-label">Gym Session</span>
                </div>
                <div className="utility-panel__schedule-item">
                  <span className="utility-panel__schedule-time">1:00 PM</span>
                  <span className="utility-panel__schedule-label">Lunch</span>
                </div>
                <div className="utility-panel__schedule-item">
                  <span className="utility-panel__schedule-time">3:00 PM</span>
                  <span className="utility-panel__schedule-label">Meeting</span>
                </div>
              </div>
            </div>

            <div className="utility-panel__section">
              <h3 className="utility-panel__title">Recent Notes</h3>
              <div className="utility-panel__notes">
                <div className="utility-panel__note">
                  <p className="utility-panel__note-text">Increase bench weight next session...</p>
                  <span className="utility-panel__note-date">Yesterday</span>
                </div>
                <div className="utility-panel__note">
                  <p className="utility-panel__note-text">Focus on form for squats...</p>
                  <span className="utility-panel__note-date">3 days ago</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  // Workout type selection
  if (isSelectingType) {
    return (
      <div className="gym-page">
        <div className="gym-page__main">
          <div className="gym-tracker gym-tracker--selecting">
            <div className="gym-tracker__type-selection">
              <h1 className="gym-tracker__type-title">Select Workout Type</h1>
              <p className="gym-tracker__type-subtitle">Choose the type of workout you're doing today</p>
              <div className="gym-tracker__type-grid">
                {WORKOUT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="gym-tracker__type-button"
                    onClick={() => selectWorkoutType(type)}
                  >
                    <span className="gym-tracker__type-icon">
                      {type === 'Push' && '🏋️'}
                      {type === 'Pull' && '💪'}
                      {type === 'Leg' && '🦵'}
                      {type === 'Arms' && '🦾'}
                      {type === 'Cardio' && '🏃'}
                      {type === 'Other' && '⚡'}
                    </span>
                    <span className="gym-tracker__type-label">{type}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="gym-tracker__cancel-selection"
                onClick={cancelWorkout}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active workout
  return (
    <div className="gym-page">
      <div className="gym-page__main">
        <div className="gym-tracker">
          {/* Timer Header */}
          <div className="gym-tracker__header">
            <div className="gym-tracker__header-top">
              <div className="gym-tracker__workout-info">
                <h1 className="gym-tracker__workout-title">{workoutType} Day</h1>
                <div className="gym-tracker__workout-meta">
                  <span className="gym-tracker__workout-date">
                    {new Date().toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <span className="gym-tracker__workout-status">
                    {timerStarted ? 'Active' : 'Waiting for first set'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="gym-tracker__finish-button"
                onClick={completeWorkout}
                disabled={totalSets === 0}
                title={totalSets === 0 ? 'Add at least one exercise to finish workout' : undefined}
              >
                Finish Workout
              </button>
            </div>
            <WorkoutTimer elapsedSeconds={elapsedTime} />
            <div className="gym-tracker__header-actions">
              <button
                type="button"
                className="gym-tracker__action-button gym-tracker__action-button--secondary"
                onClick={cancelWorkout}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Today's Overview */}
          <div className="gym-tracker__overview">
            <div className="gym-tracker__stat-card">
              <span className="gym-tracker__stat-label">Total Sets</span>
              <span className="gym-tracker__stat-value">{totalSets}</span>
            </div>
            <div className="gym-tracker__stat-card">
              <span className="gym-tracker__stat-label">Total Volume</span>
              <span className="gym-tracker__stat-value">{totalVolume.toLocaleString()} lbs</span>
            </div>
            <div className="gym-tracker__stat-card">
              <span className="gym-tracker__stat-label">Avg Intensity</span>
              <span className="gym-tracker__stat-value">
                {avgIntensity > 0 ? renderStars(avgIntensity) : '-'}
              </span>
            </div>
            <div className="gym-tracker__stat-card">
              <span className="gym-tracker__stat-label">Duration</span>
              <span className="gym-tracker__stat-value">
                {timerStarted ? formatDuration(elapsedTime) : '0m'}
              </span>
            </div>
          </div>

          {/* Exercise List */}
          <div className="gym-tracker__content">
            <div className="gym-tracker__exercises-grid">
              {Object.entries(exerciseGroups).map(([exerciseName, sets]) => (
                <ExerciseCard
                  key={exerciseName}
                  exerciseName={exerciseName}
                  sets={sets}
                  onAddSet={() => {
                    setSelectedExercise(exerciseName);
                    setShowAddSetModal(true);
                  }}
                  onRename={renameExercise}
                  onUpdateSet={updateSet}
                  onDeleteSet={deleteSet}
                />
              ))}
            </div>

            {/* Add Exercise Button */}
            <button
              type="button"
              className="gym-tracker__add-exercise"
              onClick={() => {
                setSelectedExercise(null);
                setShowAddSetModal(true);
              }}
            >
              <span className="gym-tracker__add-icon">+</span>
              Add Exercise
            </button>
          </div>

          {/* Floating Voice Logger */}
          <VoiceLogButton onSetLogged={addSet} />

          {/* Error Display */}
          {error && (
            <div className="gym-tracker__error">
              {error}
            </div>
          )}

          {/* Add Set Modal */}
          {showAddSetModal && (
            <AddSetModal
              workoutType={workoutType}
              initialExercise={selectedExercise ?? undefined}
              onClose={() => {
                setShowAddSetModal(false);
                setSelectedExercise(null);
              }}
              onSubmit={(data) => {
                addSet(data.exercise, data.reps, data.weight, data.intensity);
                setShowAddSetModal(false);
                setSelectedExercise(null);
              }}
            />
          )}
        </div>
      </div>

      {/* Right Utility Panel */}
      <aside className="gym-page__utility">
        <div className="utility-panel">
          <div className="utility-panel__section">
            <h3 className="utility-panel__title">Voice Logger</h3>
            <p className="utility-panel__description">Log sets hands-free using voice commands</p>
            <button type="button" className="utility-panel__button">
              Start Recording
            </button>
          </div>

          <div className="utility-panel__section">
            <h3 className="utility-panel__title">Today's Schedule</h3>
            <div className="utility-panel__schedule">
              <div className="utility-panel__schedule-item utility-panel__schedule-item--current">
                <span className="utility-panel__schedule-time">10:00 AM</span>
                <span className="utility-panel__schedule-label">Gym Session</span>
              </div>
              <div className="utility-panel__schedule-item">
                <span className="utility-panel__schedule-time">1:00 PM</span>
                <span className="utility-panel__schedule-label">Lunch</span>
              </div>
            </div>
          </div>

          <div className="utility-panel__section">
            <h3 className="utility-panel__title">Recent Notes</h3>
            <div className="utility-panel__notes">
              <div className="utility-panel__note">
                <p className="utility-panel__note-text">Increase bench weight next session...</p>
                <span className="utility-panel__note-date">Yesterday</span>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
